import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getOrderedCurrencies } from "@/lib/currencies";
import { toast } from "sonner";
import { toastError } from "@/lib/errors";
import { Loader2, Users, User, MailCheck, KeyRound } from "lucide-react";
import { GoogleIcon } from "@/components/GoogleIcon";
import { setPendingSignupIntent, clearPendingSignupIntent } from "@/lib/pendingSignupIntent";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const searchSchema = z.object({
  invite_code: fallback(z.string(), "").default(""),
  tab: fallback(z.enum(["signin", "signup"]), "signin").default("signin"),
});

export const Route = createFileRoute("/auth")({
  validateSearch: zodValidator(searchSchema),
  component: AuthPage,
});

type SignupMode = "choose" | "invite" | "individual";
type View = "auth" | "signupSuccess" | "recovery";

function getPasswordStrength(pw: string): { score: 0 | 1 | 2 | 3; label: string; className: string } {
  if (!pw) return { score: 0, label: "", className: "" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score++;
  if (pw.length < 6) return { score: 0, label: "Muy corta", className: "text-destructive" };
  if (score <= 1) return { score: 1, label: "Débil", className: "text-destructive" };
  if (score === 2) return { score: 2, label: "Media", className: "text-amber-600 dark:text-amber-500" };
  return { score: 3, label: "Fuerte", className: "text-emerald-600 dark:text-emerald-500" };
}

function AuthPage() {
  const navigate = useNavigate();
  const { invite_code: urlInviteCode, tab: urlTab } = useSearch({ from: "/auth" });
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [householdName, setHouseholdName] = useState("Mi Hogar");
  const [baseCurrency, setBaseCurrency] = useState("MXN");
  const [whatsapp, setWhatsapp] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [mode, setMode] = useState<SignupMode>("choose");
  const [confirmIndividual, setConfirmIndividual] = useState(false);
  const [defaultTab, setDefaultTab] = useState<"signin" | "signup">(urlInviteCode ? "signup" : urlTab);
  const [signupSuccess, setSignupSuccess] = useState<{ email: string; ambiguous: boolean } | null>(null);
  const [view, setView] = useState<View>("auth");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    // Subscribe FIRST so we don't miss the PASSWORD_RECOVERY event fired
    // when Supabase parses the magic-link hash on mount.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setView("recovery");
        setRecoveryError(null);
        try { history.replaceState(null, "", window.location.pathname + window.location.search); } catch {}
        return;
      }
      // Don't auto-redirect to dashboard while in recovery flow
      if (session && event === "SIGNED_IN") {
        setView((v) => (v === "recovery" ? v : v));
      }
    });

    // Hash fallback: if we land with #type=recovery before the event fires
    // (e.g. fast paint), force the recovery view immediately.
    if (typeof window !== "undefined" && window.location.hash.includes("type=recovery")) {
      setView("recovery");
    }

    if (urlInviteCode) {
      setInviteCode(urlInviteCode.toUpperCase());
      setMode("invite");
      setDefaultTab("signup");
    }
    supabase.auth.getSession().then(({ data }) => {
      // Only auto-redirect if we're not in the middle of a recovery flow.
      if (data.session && !window.location.hash.includes("type=recovery")) {
        // Defer one tick so PASSWORD_RECOVERY (if pending) wins the race.
        setTimeout(() => {
          setView((current) => {
            if (current !== "recovery") navigate({ to: "/dashboard" });
            return current;
          });
        }, 0);
      }
    });

    return () => { sub.subscription.unsubscribe(); };
  }, [navigate, urlInviteCode]);

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setLoading(false); return toastError(error); }

    let pendingCode: string | null = null;
    try { pendingCode = localStorage.getItem("pendingInviteCode"); } catch {}

    if (pendingCode) {
      const { error: joinErr } = await supabase.rpc("join_household_by_code", {
        _code: pendingCode,
      });
      try { localStorage.removeItem("pendingInviteCode"); } catch {}
      if (joinErr && !joinErr.message.toLowerCase().includes("already in household")) {
        toastError(joinErr, "No se pudo unir al hogar.");
      }
    }

    setLoading(false);
    navigate({ to: "/dashboard" });
  }

  async function performSignUp() {
    if (!whatsapp.trim()) {
      toast.error("El número de WhatsApp es requerido.");
      return;
    }
    setInviteError(null);
    setLoading(true);

    // If invite mode: validate code BEFORE creating the user
    let validatedHouseholdId: string | null = null;
    if (mode === "invite") {
      const code = inviteCode.trim().toUpperCase();
      if (!code) {
        setLoading(false);
        setInviteError("Ingresa un código de invitación.");
        return;
      }
      const { data: valid, error: vErr } = await supabase.rpc("check_invite_code", { _code: code });
      if (vErr || !valid) {
        setLoading(false);
        setInviteError("Código de invitación inválido o el hogar ya está completo (máx. 2 personas).");
        return;
      }
      // Look up the household id (RLS allows reading own household after join; for now use an open lookup via rpc result)
      // We'll fetch via direct select — invite_code lookup is allowed because households RLS only restricts to members; so SELECT will return null.
      // Workaround: rely on join_household_by_code after signup using the same code.
      validatedHouseholdId = "__pending__";
    }

    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          display_name: name,
          household_name: mode === "individual" ? householdName : "Mi Hogar",
          base_currency: baseCurrency,
          whatsapp: whatsapp.trim() || null,
          // Almacenado en user_metadata para sobrevivir el flujo PKCE cross-tab
          ...(mode === "invite" ? { pending_invite_code: inviteCode.trim().toUpperCase() } : {}),
        },
      },
    });
    if (error) { setLoading(false); return toastError(error); }

    // Detect duplicate email (Supabase enumeration protection: identities is empty array)
    const ambiguous = !!data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0;

    if (mode === "invite" && validatedHouseholdId && !ambiguous) {
      // Need an active session to call join_household_by_code (auth.uid())
      if (!data.session) {
        setLoading(false);
        try { localStorage.setItem("pendingInviteCode", inviteCode.trim().toUpperCase()); } catch {}
        setSignupSuccess({ email, ambiguous: false });
        return;
      }
      const { error: joinErr } = await supabase.rpc("join_household_by_code", {
        _code: inviteCode.trim().toUpperCase(),
      });
      if (joinErr) {
        setLoading(false);
        toastError(joinErr, "No se pudo unir al hogar.");
        return;
      }
      try { await supabase.auth.updateUser({ data: { pending_invite_code: null } }); } catch {}
    }

    if (ambiguous && mode === "invite") {
      try { localStorage.setItem("pendingInviteCode", inviteCode.trim().toUpperCase()); } catch {}
    }

    setLoading(false);
    if (data.session) navigate({ to: "/dashboard" });
    else setSignupSuccess({ email, ambiguous });
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    if (mode === "individual" && !confirmIndividual) {
      setConfirmIndividual(true);
      return;
    }
    await performSignUp();
  }

  const googleDisabledReason: string | null = (() => {
    if (defaultTab !== "signup") return null;
    if (mode === "choose") return "Elegí 'Tengo código' o 'Cuenta individual' antes de continuar.";
    if (mode === "invite" && !inviteCode.trim()) return "Ingresá el código de invitación primero.";
    if ((mode === "invite" || mode === "individual") && !whatsapp.trim()) return "Ingresá tu WhatsApp antes de continuar.";
    return null;
  })();

  async function handleGoogle() {
    setInviteError(null);
    if (defaultTab === "signin") {
      clearPendingSignupIntent();
    } else if (mode === "invite") {
      const code = inviteCode.trim().toUpperCase();
      if (!code) { setInviteError("Ingresá un código de invitación."); return; }
      const { data: valid, error: vErr } = await supabase.rpc("check_invite_code", { _code: code });
      if (vErr || !valid) {
        setInviteError("Código de invitación inválido o el hogar ya está completo (máx. 2 personas).");
        return;
      }
      setPendingSignupIntent({ kind: "invite", code, displayName: name.trim() || null, whatsapp: whatsapp.trim() || null });
    } else if (mode === "individual") {
      setPendingSignupIntent({
        kind: "individual",
        householdName: householdName.trim() || "Mi Hogar",
        baseCurrency,
        displayName: name.trim() || null,
        whatsapp: whatsapp.trim() || null,
      });
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin + "/dashboard" },
    });
    if (error) {
      clearPendingSignupIntent();
      toast.error("No se pudo iniciar sesión con Google");
    }
  }

  async function resendConfirmation() {
    if (!signupSuccess) return;
    const { error } = await supabase.auth.resend({ type: "signup", email: signupSuccess.email });
    if (error) return toastError(error);
    toast.success("Correo reenviado.");
  }

  function backToSignIn(prefillEmail?: string) {
    setSignupSuccess(null);
    setMode("choose");
    setPassword("");
    setDefaultTab("signin");
    if (prefillEmail) setEmail(prefillEmail);
  }

  async function handleForgotPassword(targetEmail?: string) {
    const target = (targetEmail ?? email).trim();
    if (!target) {
      toast.error("Ingresá tu email para recuperar la contraseña.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(target)) {
      toast.error("Ingresá un email válido.");
      return;
    }
    setForgotLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(target, {
      redirectTo: window.location.origin + "/auth",
    });
    setForgotLoading(false);
    if (error) return toastError(error);
    toast.success("Si el email está registrado, te enviamos un enlace.");
  }

  async function handleUpdatePassword(e: React.FormEvent) {
    e.preventDefault();
    setRecoveryError(null);
    if (newPassword.length < 6) {
      setRecoveryError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setRecoveryError("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      setLoading(false);
      setRecoveryError(error.message);
      return;
    }
    const recoveredEmail = data.user?.email ?? "";
    await supabase.auth.signOut();
    setLoading(false);
    setNewPassword("");
    setConfirmPassword("");
    setView("auth");
    setDefaultTab("signin");
    if (recoveredEmail) setEmail(recoveredEmail);
    setPassword("");
    toast.success("Contraseña actualizada. Iniciá sesión con tu nueva contraseña.");
  }

  const strength = getPasswordStrength(newPassword);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">💰 Finanzas en Pareja</CardTitle>
          <CardDescription>Gestiona tu patrimonio personal o de pareja.</CardDescription>
        </CardHeader>
        <CardContent>
          {view === "recovery" ? (
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <KeyRound className="h-7 w-7 text-primary" />
                </div>
                <h2 className="text-xl font-semibold">Nueva contraseña</h2>
                <p className="text-sm text-muted-foreground">
                  Ingresá tu nueva contraseña. Después tendrás que iniciar sesión con ella.
                </p>
              </div>
              <div>
                <Label>Nueva contraseña</Label>
                <PasswordInput
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                {newPassword && (
                  <p className={`mt-1 text-xs font-medium ${strength.className}`}>
                    Fortaleza: {strength.label}
                  </p>
                )}
              </div>
              <div>
                <Label>Confirmar contraseña</Label>
                <PasswordInput
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="mt-1 text-xs font-medium text-destructive">
                    Las contraseñas no coinciden.
                  </p>
                )}
              </div>
              {recoveryError && (
                <p className="text-sm font-medium text-destructive">{recoveryError}</p>
              )}
              <Button className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Guardar nueva contraseña
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={async () => {
                  await supabase.auth.signOut();
                  setNewPassword("");
                  setConfirmPassword("");
                  setRecoveryError(null);
                  setView("auth");
                  setDefaultTab("signin");
                }}
              >
                Cancelar
              </Button>
            </form>
          ) : signupSuccess ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <MailCheck className="h-8 w-8 text-primary" />
              </div>
              {signupSuccess.ambiguous ? (
                <>
                  <div className="space-y-1">
                    <h2 className="text-xl font-semibold">Revisa tu correo</h2>
                    <p className="text-sm text-muted-foreground">
                      Si <span className="font-medium text-foreground">{signupSuccess.email}</span> aún no estaba registrado, te enviamos un enlace para confirmar tu cuenta. Si ya tenías una cuenta con ese correo, podés iniciar sesión directamente o recuperar tu contraseña.
                    </p>
                  </div>
                  <div className="flex w-full flex-col gap-2 pt-2">
                    <Button onClick={() => backToSignIn(signupSuccess.email)}>Iniciar sesión</Button>
                    <Button variant="outline" onClick={() => handleForgotPassword(signupSuccess.email)} disabled={forgotLoading}>Olvidé mi contraseña</Button>
                    <Button variant="ghost" onClick={resendConfirmation}>Reenviar correo de confirmación</Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-1">
                    <h2 className="text-xl font-semibold">¡Casi listo!</h2>
                    <p className="text-sm text-muted-foreground">
                      Te enviamos un correo a <span className="font-medium text-foreground">{signupSuccess.email}</span>.
                      Revisa tu bandeja de entrada (y la carpeta de spam) para activar tu cuenta.
                    </p>
                  </div>
                  <div className="flex w-full flex-col gap-2 pt-2">
                    <Button onClick={() => backToSignIn()}>Volver al inicio de sesión</Button>
                    <Button variant="ghost" onClick={resendConfirmation}>Reenviar correo</Button>
                  </div>
                </>
              )}
            </div>
          ) : (
          <Tabs value={defaultTab} onValueChange={(v) => setDefaultTab(v as "signin" | "signup")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Iniciar sesión</TabsTrigger>
              <TabsTrigger value="signup">Crear cuenta</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-3">
                <div><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
                <div><Label>Contraseña</Label><PasswordInput required value={password} onChange={(e) => setPassword(e.target.value)} /></div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => handleForgotPassword()}
                    disabled={forgotLoading}
                  >
                    ¿Olvidaste tu contraseña?
                  </Button>
                </div>
                <Button className="w-full" disabled={loading}>{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Entrar</Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              {mode === "choose" ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">¿Cómo quieres comenzar?</p>
                  <button
                    type="button"
                    onClick={() => setMode("invite")}
                    className="w-full rounded-lg border-2 border-primary/40 p-4 text-left transition hover:border-primary hover:bg-primary/5"
                  >
                    <div className="flex items-center gap-3">
                      <Users className="h-6 w-6 text-primary" />
                      <div>
                        <p className="font-medium">Tengo un código de invitación</p>
                        <p className="text-xs text-muted-foreground">Únete al hogar de tu pareja.</p>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("individual")}
                    className="w-full rounded-lg border p-4 text-left transition hover:bg-muted"
                  >
                    <div className="flex items-center gap-3">
                      <User className="h-6 w-6 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Crear una cuenta individual</p>
                        <p className="text-xs text-muted-foreground">Empieza solo, podrás invitar después.</p>
                      </div>
                    </div>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSignUp} className="space-y-3">
                  <button
                    type="button"
                    onClick={() => { setMode("choose"); setInviteError(null); }}
                    className="text-xs text-muted-foreground underline"
                  >
                    ← Cambiar opción
                  </button>
                  <div><Label>Nombre</Label><Input required value={name} onChange={(e) => setName(e.target.value)} /></div>
                  <div><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
                  <div><Label>Contraseña</Label><PasswordInput required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} /></div>
                  <div>
                    <Label>WhatsApp <span className="text-muted-foreground font-normal text-xs">(obligatorio)</span></Label>
                    <Input
                      required
                      type="tel"
                      placeholder="+54 9 11 1234-5678"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value)}
                    />
                  </div>

                  {mode === "invite" ? (
                    <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
                      <Label>Código de invitación</Label>
                      <Input
                        required
                        placeholder="ABC12345"
                        value={inviteCode}
                        onChange={(e) => { setInviteCode(e.target.value.toUpperCase()); setInviteError(null); }}
                      />
                      {inviteError && (
                        <p className="text-sm font-medium text-destructive">{inviteError}</p>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-md border p-3 space-y-3">
                      <p className="text-xs font-medium text-muted-foreground">Tu nuevo hogar</p>
                      <div><Label>Nombre del hogar</Label><Input value={householdName} onChange={(e) => setHouseholdName(e.target.value)} /></div>
                      <div>
                        <Label>Moneda base</Label>
                        {(() => {
                          const { featured, rest } = getOrderedCurrencies(baseCurrency);
                          return (
                            <Select value={baseCurrency} onValueChange={setBaseCurrency}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {featured.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                                {featured.length > 0 && rest.length > 0 && <SelectSeparator />}
                                {rest.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  <Button className="w-full" disabled={loading}>
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Crear cuenta
                  </Button>
                </form>
              )}
            </TabsContent>
          </Tabs>
          )}

          {view !== "recovery" && !signupSuccess && (
            <>
              <div className="my-4 flex items-center gap-2"><div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">o</span><div className="h-px flex-1 bg-border" /></div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleGoogle}
                disabled={!!googleDisabledReason}
              >
                <GoogleIcon className="mr-2 h-4 w-4" />
                Continuar con Google
              </Button>
              {googleDisabledReason && (
                <p className="mt-2 text-center text-xs text-muted-foreground">{googleDisabledReason}</p>
              )}
              {defaultTab === "signup" && !googleDisabledReason && (
                <p className="mt-2 text-center text-xs text-muted-foreground">
                  Respetaremos {mode === "invite" ? "tu código de invitación" : "el nombre y moneda del hogar"} al volver de Google.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmIndividual} onOpenChange={setConfirmIndividual}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Crear cuenta individual?</AlertDialogTitle>
            <AlertDialogDescription>
              Estás por crear una cuenta SIN unirte a ningún hogar existente. Si tu pareja te envió un código de invitación, vuelve atrás y elige "Tengo un código de invitación".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmIndividual(false)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={async () => { setConfirmIndividual(false); await performSignUp(); }}>
              Sí, continuar solo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}